import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Button, Empty, Spin, message } from 'antd'
import {
  ToolOutlined,
  TruckOutlined,
  DesktopOutlined,
  FireOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { toolsService } from '@/renderer/services/toolsService'
import './InventoryHub.css'

interface EquipmentStats {
  name: string
  path: string
  icon: React.ReactNode
  description: string
  color: string
  count: number
}

export function InventoryHub() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<EquipmentStats[]>([])

  useEffect(() => {
    loadEquipmentStats()
  }, [])

  const loadEquipmentStats = async () => {
    setLoading(true)
    try {
      // Load tools count
      const toolsResponse = await toolsService.listTools(1, 1)
      const toolsCount = toolsResponse.total || 0

      // Initialize equipment stats
      // Note: These will be updated once we have the service layer for each type
      const equipmentStats: EquipmentStats[] = [
        {
          name: 'Tools',
          path: '/inventory/tools',
          icon: <ToolOutlined />,
          description: 'Downhole tools and wireline equipment',
          color: '#1890ff',
          count: toolsCount,
        },
        {
          name: 'Units',
          path: '/inventory/units',
          icon: <TruckOutlined />,
          description: 'Trucks, reels, and cables',
          color: '#52c41a',
          count: 0, // Will be updated when UnitsService is created
        },
        {
          name: 'Panels',
          path: '/inventory/panels',
          icon: <DesktopOutlined />,
          description: 'Electronic panels (DAP, Conditional, Winch)',
          color: '#fa8c16',
          count: 0, // Will be updated when PanelsService is created
        },
        {
          name: 'Pressure Equipment',
          path: '/inventory/pressure-equipment',
          icon: <FireOutlined />,
          description: 'BOP, chokes, and pressure control equipment',
          color: '#eb2f96',
          count: 0, // Will be updated when PressureEquipmentService is created
        },
        {
          name: 'Consumables',
          path: '/inventory/consumables',
          icon: <ShoppingOutlined />,
          description: 'Lubricants, seals, and other consumables',
          color: '#722ed1',
          count: 0, // Will be updated when ConsumablesService is created
        },
      ]

      setStats(equipmentStats)
    } catch (error) {
      console.error('Error loading equipment stats:', error)
      message.warning('Unable to connect to backend - showing default view')

      // Set default stats with zero counts so cards still render
      const defaultStats: EquipmentStats[] = [
        {
          name: 'Tools',
          path: '/inventory/tools',
          icon: <ToolOutlined />,
          description: 'Downhole tools and wireline equipment',
          color: '#1890ff',
          count: 0,
        },
        {
          name: 'Units',
          path: '/inventory/units',
          icon: <TruckOutlined />,
          description: 'Trucks, reels, and cables',
          color: '#52c41a',
          count: 0,
        },
        {
          name: 'Panels',
          path: '/inventory/panels',
          icon: <DesktopOutlined />,
          description: 'Electronic panels (DAP, Conditional, Winch)',
          color: '#fa8c16',
          count: 0,
        },
        {
          name: 'Pressure Equipment',
          path: '/inventory/pressure-equipment',
          icon: <FireOutlined />,
          description: 'BOP, chokes, and pressure control equipment',
          color: '#eb2f96',
          count: 0,
        },
        {
          name: 'Consumables',
          path: '/inventory/consumables',
          icon: <ShoppingOutlined />,
          description: 'Lubricants, seals, and other consumables',
          color: '#722ed1',
          count: 0,
        },
      ]

      setStats(defaultStats)
    } finally {
      setLoading(false)
    }
  }

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: 600 }}>Equipment Inventory</h1>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Manage all equipment types: tools, units, panels, pressure equipment, and consumables
        </p>
      </div>

      {stats.length === 0 ? (
        <Empty description="No equipment types available" />
      ) : (
        <Row gutter={[24, 24]}>
          {stats.map((stat) => (
            <Col key={stat.path} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                onClick={() => handleNavigate(stat.path)}
                style={{
                  borderTop: `4px solid ${stat.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)'
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '48px', color: stat.color, marginBottom: '12px' }}>
                    {stat.icon}
                  </div>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600 }}>
                    {stat.name}
                  </h2>
                  <p style={{ margin: '0', color: '#666', fontSize: '12px' }}>
                    {stat.description}
                  </p>
                </div>

                <div style={{ textAlign: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
                  <Statistic
                    value={stat.count}
                    suffix="items"
                    valueStyle={{ fontSize: '24px', fontWeight: 600, color: stat.color }}
                  />
                </div>

                <Button
                  type="primary"
                  block
                  style={{ marginTop: '12px', backgroundColor: stat.color, borderColor: stat.color }}
                  icon={<ArrowRightOutlined />}
                >
                  Manage {stat.name}
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  )
}
